import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Circle, Trash2, Plus, Mic, ListChecks } from "lucide-react";
import { VoiceRecorder } from "@/components/VoiceRecorder";

const dateFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function TodosPage() {
  const [todos, setTodos] = useState([]);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("open"); // open | all | done

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/todos");
      setTodos(data || []);
    } catch {
      toast.error("Could not load todos");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addTodo = async () => {
    const t = draft.trim();
    if (!t) return;
    setAdding(true);
    try {
      await api.post("/todos", { text: t });
      setDraft("");
      await load();
    } catch {
      toast.error("Could not add");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (todo) => {
    try {
      await api.put(`/todos/${todo.id}`, { done: !todo.done });
      await load();
    } catch { toast.error("Could not update"); }
  };

  const remove = async (todo) => {
    if (!window.confirm("Delete this todo?")) return;
    try {
      await api.delete(`/todos/${todo.id}`);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const visible = todos.filter((t) => filter === "all" ? true : filter === "open" ? !t.done : t.done);
  const openCount = todos.filter((t) => !t.done).length;
  const doneCount = todos.length - openCount;

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Dialog Healthcare · Notes to self</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Todo list</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">Tap the mic and rapid-fire whatever you need to remember. Whisper transcribes it and each sentence becomes its own todo.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
        <StatTile label="Open" value={openCount} active={filter === "open"} onClick={() => setFilter("open")} />
        <StatTile label="Done" value={doneCount} active={filter === "done"} onClick={() => setFilter("done")} />
        <StatTile label="All" value={todos.length} active={filter === "all"} onClick={() => setFilter("all")} />
      </div>

      <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-5 flex items-center gap-4 shadow-sm">
        <VoiceRecorder onSaved={load} />
        <div className="flex-1">
          <div className="text-sm font-medium text-[#292723]">Voice todo</div>
          <div className="text-xs text-[#8A847A] mt-0.5">Hit the mic → talk → hit stop. Each sentence becomes its own todo.</div>
        </div>
      </div>

      <div className="mt-4 bg-white border border-[#E5E0D8] rounded-md p-4">
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addTodo(); }}
            placeholder="Type a todo and hit Enter…"
            className="bg-white border-[#E5E0D8] h-10"
            data-testid="todo-input"
          />
          <Button onClick={addTodo} disabled={!draft.trim() || adding} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="todo-add-btn">
            <Plus className="h-4 w-4 mr-1"/> Add
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-2" data-testid="todo-list">
        {visible.length === 0 ? (
          <div className="bg-white border border-[#E5E0D8] rounded-md p-10 text-center">
            <ListChecks className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
            <p className="text-sm text-[#8A847A] mt-3">
              {filter === "open" ? "You're all caught up." : filter === "done" ? "Nothing completed yet." : "No todos yet — hit the mic above."}
            </p>
          </div>
        ) : visible.map((t) => (
          <div key={t.id} className={`bg-white border border-[#E5E0D8] rounded-md p-3 flex items-start gap-3 ${t.done ? "opacity-60" : ""}`} data-testid={`todo-row-${t.id}`}>
            <button
              onClick={() => toggle(t)}
              className="mt-0.5 text-[#4A6B5D] hover:text-[#3E594D]"
              aria-label={t.done ? "Mark open" : "Mark done"}
              data-testid={`todo-toggle-${t.id}`}
            >
              {t.done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-sm text-[#292723] ${t.done ? "line-through" : ""}`}>{t.text}</div>
              <div className="flex items-center gap-2 mt-1 text-[11px] text-[#8A847A]">
                {t.source === "voice" && <span className="inline-flex items-center gap-1"><Mic className="h-3 w-3"/> voice</span>}
                <span>{t.created_at ? dateFmt.format(new Date(t.created_at)) : ""}</span>
              </div>
            </div>
            <button
              onClick={() => remove(t)}
              className="text-[#8A847A] hover:text-[#C17D6E] p-1"
              aria-label="Delete todo"
              data-testid={`todo-delete-${t.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md p-3 text-left border transition-colors ${
        active ? "bg-[#4A6B5D] border-[#4A6B5D] text-white" : "bg-white border-[#E5E0D8] text-[#5C5850] hover:bg-[#F7F5F0]"
      }`}
      data-testid={`todo-filter-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-[0.1em] opacity-80">{label}</div>
      <div className="font-mono font-semibold text-xl mt-0.5">{value}</div>
    </button>
  );
}
